import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoggerService, dateTimeToCustomFormat } from '../../../ui/utils';
import { useAPICall } from '../../../hooks';
import {
  AdminPageHeader,
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  ButtonWidth,
  Drawer,
  Label,
  Modal,
  TableWithSearchTemplate,
  Tag,
  TagStyle,
  Theme,
} from '../../../ui/components';
import { useAdminToast } from '../../../ui/components/AdminToast/AdminToast';
import CampaignService from '../../../sdk/services/campaignService';
import OperationCenterErrorCard, {
  ErrorType,
} from '../../../ui/components/OperationCenterErrorCard/OperationCenterErrorCard';
import getCampaignListConfig, { campaignSearchOptions } from './campaignListConfig';
import styles from './index.module.scss';

interface CampaignData {
  recId: number;
  campaignId: string;
  campaignName: string;
  campaignStart: string;
  campaignEnd: string;
  status: string;
  createdDate: string;
}

export const AdminCampaignList = () => {
  const { t } = useTranslation();
  const { adminToast } = useAdminToast();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<CampaignData | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<CampaignData | null>(null);
  const [showDelete, setShowDelete] = useState(false);
  const [displayEmpty, setDisplayEmpty] = useState(false);
  const apiCall = useAPICall();

  const fetchCampaignList = async () => {
    try {
      const result = await apiCall(CampaignService?.getAllCampaigns);
      if (result.status === 200 && result?.data?.data?.length > 0) {
        setData(result?.data?.data);
      } else {
        if (result?.data?.data === null) {
          adminToast({
            message: t('admin.userEdit.noDataAvailable'),
            theme: Theme.SUCCESS,
          });
        }
        setDisplayEmpty(true);
      }
    } catch (error) {
      setDisplayEmpty(true);
      LoggerService.log('Failed to retrieve campaign list', error);
    }
  };

  useEffect(() => {
    fetchCampaignList();
  }, []);

  const onEdit = (id: string) => {
    navigate(`/admin/campaign-configuration/edit?=${id}`);
  };

  const onClick = (id: CampaignData) => {
    //to show pop up modal
    setSelectedRowId(id);
    setShowDelete(true);
  };

  const buttonConfig = [
    {
      title: t('admin.campaignConfig.addCampaign'),
      buttonStyle: ButtonStyles.SOLID,
      onClick: () => {
        navigate(`/admin/campaign-configuration/edit?=new`);
      },
    },
  ];
  const onDeleteAction = async () => {
    try {
      await apiCall(CampaignService.deleteCampaign, `${selectedRowId?.recId ?? selectedRow?.recId}`);

      // Update the table after deletion
      adminToast({
        message: t('admin.campaignConfig.deleteCampaignSuccess').replace(
          '{0}',
          selectedRow?.campaignName ?? (selectedRowId?.campaignName as string),
        ),
        theme: Theme.SUCCESS,
      });
      fetchCampaignList();
      setDrawerOpen(false);
      setShowDelete(false);
    } catch (error) {
      LoggerService.log('Error deleting user', error);
      // Handle error, show a notification, etc.
    }
  };

  const deletePrompt = (
    <>
      {showDelete && (
        <Modal
          primaryButtonLabel={''}
          headline={`${t('admin.campaignConfig.deleteCampaign')} ${
            selectedRow?.campaignId ?? selectedRowId?.campaignId
          }?`}
          closeIcon={false}
          containerWidth={'460px'}
          primaryButton={
            <Button
              title={t('admin.userSetup.confirm')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                onDeleteAction();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('admin.userSetup.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                setShowDelete(false);
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        />
      )}
    </>
  );

  const drawerDetails = (
    <>
      {drawerOpen && (
        <Drawer
          onClose={() => setDrawerOpen(false)}
          closeOnBackdropClick={true}
          closeIcon={true}
          bottomGapFooter="0px"
          header={<div>{t('admin.campaignConfig.campaignDetails')}</div>}
          footer={
            <div className={styles['button-container']}>
              <Button
                title="Delete"
                onClick={() => setShowDelete(true)}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
              <Button
                title="Edit"
                onClick={() => onEdit(selectedRow ? selectedRow.recId.toString() : '')}
                size={ButtonSize.SMALL}
                buttonWidth={ButtonWidth.FULL_WIDTH}
              />
            </div>
          }
        >
          <div className={styles['summary-pane-body']}>
            <div className={styles['basic-details']}>
              <Tag
                text={selectedRow ? selectedRow.status : ''}
                style={TagStyle.OUTLINE}
                theme={
                  selectedRow?.status === 'ONGOING'
                    ? Theme.SUCCESS
                    : selectedRow?.status === 'NEW'
                      ? Theme.WARNING
                      : Theme.ERROR
                }
                className={styles['status-tag']}
              />
              <div className={styles['title-value']}>
                <Label title={t('admin.campaignConfig.campaignId')} value={selectedRow?.campaignId} />
                <Label title={t('admin.campaignConfig.campaignName')} value={selectedRow?.campaignName} />
              </div>
              <div className={styles['horizontal-divider']} />
              <div className={styles['title-value']}>
                <Label
                  title={t('admin.campaignConfig.createdDate')}
                  value={dateTimeToCustomFormat(selectedRow ? selectedRow.createdDate : '', 'DD/MM/yyyy')}
                />
              </div>
              <div className={styles['two-columns']}>
                <div className={`${styles['title-value']} ${styles['vertical-divider']}`}>
                  <Label
                    title={t('admin.campaignConfig.startDate')}
                    value={dateTimeToCustomFormat(selectedRow ? selectedRow.campaignStart : '', 'DD/MM/yyyy')}
                  />
                </div>
                <div className={`${styles['title-value']}`}>
                  <Label
                    title={t('admin.campaignConfig.endDate')}
                    value={dateTimeToCustomFormat(selectedRow ? selectedRow.campaignEnd : '', 'DD/MM/yyyy')}
                  />
                </div>
              </div>
            </div>
          </div>
        </Drawer>
      )}
    </>
  );

  return (
    <div>
      <AdminPageHeader title={t('admin.campaignConfig.title')} button={buttonConfig} />
      {displayEmpty ? (
        <div className={styles['no-results-container']}>
          <OperationCenterErrorCard errorType={ErrorType.NO_RECORD} />
        </div>
      ) : (
        <TableWithSearchTemplate
          dataSource={data}
          handleReset={undefined}
          handleSearch={undefined}
          searchByStrip
          getColumns={getCampaignListConfig}
          data={campaignSearchOptions}
          rowKey="campaignId"
          selected={selectedRow?.campaignId}
          cb={{ onEdit, onClick }}
          onRowClick={row => {
            setSelectedRow(row);
            setDrawerOpen(true);
          }}
        />
      )}
      {drawerDetails}
      {deletePrompt}
    </div>
  );
};
